/**
 * 1.4 P24: existing-customer import — template, upload, preview, options,
 * confirm. Works for owners and admins acting for an owner (useOwnerScope).
 * Commit calls POST /api/owner/imports/:id/commit (P25).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Platform, Switch } from 'react-native';
import { useRouter, Stack } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';

import { useOwnerScope } from '../context/OwnerScopeContext';
import { useTheme } from '../context/ThemeContext';
import { useToast } from '../components/Toast';
import { uploadMultipart } from '../api/upload';
import { pickCsvFile } from '../utils/documentPicker';
import { exportCsvFromUrl } from '../utils/fileExport';
import { config } from '../config';
import { getToken, getCsrfToken } from '../api/client';
import ContentContainer from '../components/ContentContainer';
import Card from '../components/Card';
import Button from '../components/Button';
import Input from '../components/Input';
import Picker from '../components/Picker';
import InlineConfirm from '../components/InlineConfirm';
import type { ColorTheme } from '../utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../utils/theme';

interface RowMsg { code: string; message: string; level: 'error' | 'warning' }
interface PreviewRow { rowNumber: number; email: string | null; firstName: string | null; lastName: string | null; phone: string | null; planName: string | null; planId: string | null; nextBillDate: string | null; vehiclePlate: string | null; status: string; messages: RowMsg[] }
interface UploadResult { importId: string; counts: { ok: number; warning: number; error: number; total: number }; rows: PreviewRow[]; truncated: boolean; errorsCsvUrl: string }
interface Plan { id: string; name: string; isActive: boolean; priceMonthly?: number }

function firstOfNextMonth(): string {
  const d = new Date(); const n = new Date(d.getFullYear(), d.getMonth() + 1, 1);
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-01`;
}

export default function CustomerImportScreen() {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const router = useRouter();
  const toast = useToast();
  const scope = useOwnerScope();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [file, setFile] = useState<{ name: string; blob?: Blob; uri?: string; type?: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<UploadResult | null>(null);
  const [filter, setFilter] = useState<'all' | 'ok' | 'warning' | 'error'>('all');
  const [billingMode, setBillingMode] = useState<'active_grace' | 'pending_setup'>('active_grace');
  const [graceDays, setGraceDays] = useState('30');
  const [firstBillDate, setFirstBillDate] = useState(firstOfNextMonth());
  const [defaultPlanId, setDefaultPlanId] = useState('');
  const [sendInvites, setSendInvites] = useState(true);
  const [confirm, setConfirm] = useState(false);
  const [committing, setCommitting] = useState(false);
  const inputRef = useRef<any>(null);

  useEffect(() => {
    scope.api.get<Plan[]>('/api/subscription-plans').then(l => setPlans((Array.isArray(l) ? l : []).filter(p => p.isActive))).catch(() => setPlans([]));
  }, [scope.api]);

  const authHeaders = (): Record<string, string> => {
    const h: Record<string, string> = {};
    if (Platform.OS === 'web') { h['X-Client-Type'] = 'web'; const c = getCsrfToken(); if (c) h['X-CSRF-Token'] = c; }
    else { const t = getToken(); if (t) h['Authorization'] = `Bearer ${t}`; }
    return h;
  };
  const downloadTemplate = () => exportCsvFromUrl(`${config.apiUrl}${scope.api.scopePath('/api/owner/imports/template.csv')}`, authHeaders(), 'rinsepay-customer-import-template.csv').catch(err => toast.show({ type: 'error', title: 'Download failed', message: err.message }));
  const downloadIssues = () => result && exportCsvFromUrl(`${config.apiUrl}${scope.api.scopePath(result.errorsCsvUrl)}`, authHeaders(), 'import-issues.csv').catch(err => toast.show({ type: 'error', title: 'Download failed', message: err.message }));

  const pickNative = async () => {
    try {
      const res = await pickCsvFile();
      if (res) setFile({ name: res.name, uri: res.uri, type: res.type });
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Could not open the file', message: err.message });
    }
  };

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      if (file.blob) form.append('file', file.blob, file.name);
      else form.append('file', { uri: file.uri, name: file.name, type: file.type || 'text/csv' } as any);
      if (defaultPlanId) form.append('defaultPlanId', defaultPlanId);
      const r = await uploadMultipart<UploadResult>(scope.api.scopePath('/api/owner/imports'), form);
      setResult(r); setFilter('all');
      toast.show({ type: r.counts.error ? 'info' : 'success', title: `Checked ${r.counts.total} rows`, message: `${r.counts.ok} ready, ${r.counts.warning} with warnings, ${r.counts.error} with errors` });
    } catch (err: any) {
      const d = err?.details as { details?: { missing?: string[] } } | undefined;
      toast.show({ type: 'error', title: 'File rejected', message: d?.details?.missing?.length ? `Missing columns: ${d.details.missing.join(', ')}` : err.message });
    } finally { setUploading(false); }
  };

  const commit = async () => {
    if (!result) return;
    setConfirm(false); setCommitting(true);
    try {
      await scope.api.post(`/api/owner/imports/${result.importId}/commit`, {
        billingMode, graceDays: billingMode === 'active_grace' ? parseInt(graceDays) || 30 : null,
        firstBillDate: billingMode === 'active_grace' ? firstBillDate : null, defaultPlanId: defaultPlanId || null, sendInvites,
      });
      toast.show({ type: 'success', title: 'Import started', message: 'Accounts and memberships are being created. Follow progress on the import page.' });
      router.replace(scope.href(`/owner/customers/imports/${result.importId}`) as never);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Could not start the import', message: err.message });
    } finally { setCommitting(false); }
  };

  const importable = result ? result.counts.ok + result.counts.warning : 0;
  const blankPlanRows = result ? result.rows.filter(r => r.messages.some(m => m.code === 'PLAN_BLANK')).length : 0;
  const shown = result ? result.rows.filter(r => filter === 'all' || r.status === filter) : [];
  const planOptions = [{ value: '', label: 'No default (rows must name a plan)' }, ...plans.map(p => ({ value: p.id, label: p.name }))];

  return (
    <>
      <Stack.Screen options={{ title: 'Import Customers' }} />
      <ScrollView style={styles.container} contentContainerStyle={styles.content}>
        <ContentContainer maxWidth={960}>
          <Card style={styles.card}>
            <Text style={styles.title}>1 · Download the template</Text>
            <Text style={styles.help}>One row per customer: email, first and last name, phone, plan name (must match one of your active plans, or leave blank and pick a default below), next bill date (YYYY-MM-DD), vehicle plate / make / model, notes.</Text>
            <Button title="Download template CSV" variant="outline" onPress={downloadTemplate} icon={<Ionicons name="download-outline" size={16} color={colors.primary} />} style={{ alignSelf: 'flex-start' }} />
          </Card>

          <Card style={styles.card}>
            <Text style={styles.title}>2 · Upload your list</Text>
            <Picker label="Default plan for rows with a blank plan name" options={planOptions} value={defaultPlanId} onValueChange={setDefaultPlanId} />
            {Platform.OS === 'web' ? (
              <View style={styles.fileRow}>
                {React.createElement('input', { ref: inputRef, type: 'file', accept: '.csv,text/csv', style: { display: 'none' }, onChange: (e: any) => { const f = e.target.files?.[0]; if (f) setFile({ name: f.name, blob: f }); } })}
                <Button title={file ? 'Choose a different file' : 'Choose CSV file'} variant="outline" onPress={() => inputRef.current?.click()} />
                {!!file && <Text style={styles.fileName}>{file.name}</Text>}
              </View>
            ) : (
              <View style={styles.fileRow}>
                <Button title={file ? 'Choose a different file' : 'Choose CSV file'} variant="outline" onPress={pickNative} />
                {!!file && <Text style={styles.fileName}>{file.name}</Text>}
              </View>
            )}
            <Button title={result ? 'Check again' : 'Check the file'} onPress={upload} loading={uploading} disabled={!file} style={{ alignSelf: 'flex-start', marginTop: spacing.sm }} />
            <Text style={styles.help}>Nothing is created yet. We check every row and show you what would happen.</Text>
          </Card>

          {result && (
            <Card style={styles.card}>
              <Text style={styles.title}>3 · Review</Text>
              <View style={styles.counts}>
                {([['all', result.counts.total, 'rows'], ['ok', result.counts.ok, 'ready'], ['warning', result.counts.warning, 'warnings'], ['error', result.counts.error, 'errors']] as const).map(([k, n, label]) => (
                  <TouchableOpacity key={k} style={[styles.countChip, filter === k && styles.countChipActive, k === 'error' && n > 0 && styles.countChipDanger]} onPress={() => setFilter(k)}>
                    <Text style={[styles.countNum, filter === k && { color: colors.primary }]}>{n}</Text>
                    <Text style={styles.countLabel}>{label}</Text>
                  </TouchableOpacity>
                ))}
                {(result.counts.error > 0 || result.counts.warning > 0) && <Button title="Issues CSV" variant="outline" size="sm" onPress={downloadIssues} />}
              </View>
              {result.counts.error > 0 && <Text style={styles.warn}>Rows with errors are skipped. Fix them in your file and check again, or continue with the {importable} importable rows.</Text>}
              {blankPlanRows > 0 && !defaultPlanId && <Text style={styles.warn}>{blankPlanRows} row{blankPlanRows === 1 ? '' : 's'} have no plan name: pick a default plan in step 2 (and check again) or they will fail at import.</Text>}
              <ScrollView horizontal showsHorizontalScrollIndicator>
                <View>
                  <View style={[styles.tr, styles.th]}>
                    {['#', 'Status', 'Email', 'Name', 'Phone', 'Plan', 'Next bill', 'Plate', 'Notes'].map((h, i) => <Text key={h} style={[styles.td, styles.thText, i === 0 ? styles.tdNum : i === 1 ? styles.tdStatus : i === 8 ? styles.tdWide : i === 2 ? styles.tdEmail : null]}>{h}</Text>)}
                  </View>
                  {shown.map(r => (
                    <View key={r.rowNumber} style={[styles.tr, r.status === 'error' && styles.trError, r.status === 'warning' && styles.trWarn]}>
                      <Text style={[styles.td, styles.tdNum]}>{r.rowNumber}</Text>
                      <Text style={[styles.td, styles.tdStatus, r.status === 'error' && { color: colors.danger }, r.status === 'warning' && { color: colors.warning }]}>{r.status}</Text>
                      <Text style={[styles.td, styles.tdEmail]} numberOfLines={1}>{r.email ?? '—'}</Text>
                      <Text style={styles.td} numberOfLines={1}>{[r.firstName, r.lastName].filter(Boolean).join(' ')}</Text>
                      <Text style={styles.td}>{r.phone ?? ''}</Text>
                      <Text style={styles.td} numberOfLines={1}>{r.planName ?? (r.planId ? '(default)' : '')}</Text>
                      <Text style={styles.td}>{r.nextBillDate ?? ''}</Text>
                      <Text style={styles.td}>{r.vehiclePlate ?? ''}</Text>
                      <Text style={[styles.td, styles.tdWide]} numberOfLines={2}>{r.messages.map(m => m.message).join(' · ')}</Text>
                    </View>
                  ))}
                  {result.truncated && <Text style={styles.help}>Showing the first {result.rows.length} rows; the issues CSV has every row.</Text>}
                </View>
              </ScrollView>
            </Card>
          )}

          {result && importable > 0 && (
            <Card style={styles.card}>
              <Text style={styles.title}>4 · Options</Text>
              <Text style={styles.label}>Billing mode</Text>
              {([
                ['active_grace', 'Active now, card required by a date', 'Memberships work immediately. Customers must add a card within the grace period; the first charge happens on the first bill date.'],
                ['pending_setup', 'Pending until the customer finishes setup', 'Memberships are created but washes are blocked until the customer sets a password and adds a card.'],
              ] as const).map(([k, label, help]) => (
                <TouchableOpacity key={k} style={[styles.radioRow, billingMode === k && styles.radioRowActive]} onPress={() => setBillingMode(k)} accessibilityRole="radio" accessibilityState={{ selected: billingMode === k }}>
                  <Ionicons name={billingMode === k ? 'radio-button-on' : 'radio-button-off'} size={20} color={colors.primary} />
                  <View style={{ flex: 1 }}><Text style={styles.radioLabel}>{label}</Text><Text style={styles.help}>{help}</Text></View>
                </TouchableOpacity>
              ))}
              {billingMode === 'active_grace' && (
                <View style={styles.row2}>
                  <View style={{ flex: 1 }}><Input label="Grace period (days to add a card)" value={graceDays} onChangeText={v => setGraceDays(v.replace(/[^0-9]/g, ''))} keyboardType="number-pad" /></View>
                  <View style={{ flex: 1 }}><Input label="First bill date (YYYY-MM-DD) for rows without one" value={firstBillDate} onChangeText={setFirstBillDate} autoCapitalize="none" /></View>
                </View>
              )}
              <View style={styles.switchRow}>
                <Text style={styles.radioLabel}>Send invitation emails now</Text>
                <Switch value={sendInvites} onValueChange={setSendInvites} trackColor={{ false: colors.borderLight, true: colors.primary + '80' }} thumbColor={sendInvites ? colors.primary : colors.textLight} />
              </View>
              <Text style={styles.help}>New customers get a "set your password" email with the install + scan tutorial; existing accounts get a "your membership is ready" email. You can resend later from the import page.</Text>
              <Button title={`Import ${importable} customer${importable === 1 ? '' : 's'}`} onPress={() => setConfirm(true)} loading={committing} size="lg" style={{ marginTop: spacing.md }} />
              <InlineConfirm visible={confirm} message={`Create accounts and ${billingMode === 'active_grace' ? 'active' : 'pending'} memberships for ${importable} customers${sendInvites ? ' and email them now' : ''}? This cannot be undone in bulk.`} confirmLabel="Import" onConfirm={commit} onCancel={() => setConfirm(false)} />
            </Card>
          )}
        </ContentContainer>
      </ScrollView>
    </>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: spacing.lg },
  card: { marginBottom: spacing.md },
  title: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.xs },
  help: { fontSize: fontSize.xs, color: c.textSecondary, marginBottom: spacing.sm },
  warn: { fontSize: fontSize.sm, color: c.warning, fontWeight: fontWeight.semibold, marginBottom: spacing.sm },
  label: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text, marginBottom: spacing.xs },
  fileRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.md, flexWrap: 'wrap', marginTop: spacing.xs },
  fileName: { fontSize: fontSize.sm, color: c.text },
  counts: { flexDirection: 'row', gap: spacing.sm, flexWrap: 'wrap', alignItems: 'center', marginBottom: spacing.sm },
  countChip: { paddingHorizontal: spacing.md, paddingVertical: spacing.xs, borderRadius: borderRadius.md, borderWidth: 1, borderColor: c.border, alignItems: 'center', minWidth: 72 },
  countChipActive: { borderColor: c.primary, backgroundColor: c.primary + '12' },
  countChipDanger: { borderColor: c.danger },
  countNum: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text },
  countLabel: { fontSize: 10, color: c.textSecondary },
  tr: { flexDirection: 'row', borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: c.borderLight, paddingVertical: 4 },
  th: { backgroundColor: c.surfaceSecondary },
  thText: { fontWeight: fontWeight.semibold, color: c.textSecondary },
  trError: { backgroundColor: c.danger + '0d' },
  trWarn: { backgroundColor: c.warning + '0d' },
  td: { width: 130, fontSize: fontSize.xs, color: c.text, paddingHorizontal: 6 },
  tdNum: { width: 40 },
  tdStatus: { width: 70, fontWeight: fontWeight.semibold },
  tdEmail: { width: 200 },
  tdWide: { width: 320 },
  radioRow: { flexDirection: 'row', gap: spacing.sm, alignItems: 'flex-start', padding: spacing.sm, borderRadius: borderRadius.md, borderWidth: 1, borderColor: c.border, marginBottom: spacing.xs },
  radioRowActive: { borderColor: c.primary, backgroundColor: c.primary + '0d' },
  radioLabel: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  row2: { flexDirection: 'row', gap: spacing.sm, flexWrap: 'wrap' },
  switchRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: spacing.md, paddingVertical: spacing.sm },
});
